import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../Hook/AuthProvider";
import LoginModal from "../Components/Auth/LoginModal";
import PolicyModal from "../Components/PolicyModal";
import FestiveThemeOverlay, {
  normalizeTheme,
} from "../Components/FestiveThemeOverlay";
import MentorMindResponse from "../Components/MentorMindResponse";
import { fetchActiveTheme } from "../services/dashboardService";
import {
  initGuestSession,
  getOrCreateGuestUuid,
  getTodayCurrentAffairs,
} from "../services/guestService";
import { sendDemoMentorMind } from "../services/demoMentorMind";
import {
  Send,
  Heart,
  Menu,
  X,
  Edit3,
  BookOpen,
  Calendar,
  FileText,
  LogIn,
  ExternalLink,
  Download,
  Newspaper,
  CheckCircle2,
  Sparkles,
  ArrowRight,
  Search,
  Filter,
  CheckSquare,
  Clock,
  Layers,
  BarChart2,
  Bookmark,
} from "lucide-react";
import "../Sidebar.css";

const FESTIVE_ASSET_BASE = "https://yookistorage.blob.core.windows.net/assets";

const getDeviceType = () => {
  if (typeof window === "undefined" || !navigator) return "desktop";

  // Testing override via URL parameter (?device=android | ?device=ios | ?device=desktop)
  try {
    const params = new URLSearchParams(window.location.search);
    const override = (params.get("device") || params.get("preview_device") || "").toLowerCase();
    if (override === "android") return "android";
    if (override === "ios" || override === "ipad" || override === "iphone") return "ios";
    if (override === "desktop") return "desktop";
  } catch (e) {}

  const ua = navigator.userAgent || navigator.vendor || window.opera || "";

  // Android
  if (/Android/i.test(ua)) return "android";

  // iOS / iPadOS (includes iPad Safari on iOS 13+ which reports MacIntel with touch points)
  const isIOS =
    /iPad|iPhone|iPod/i.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (isIOS) return "ios";

  // Other mobile/tablet
  if (/Mobi|Tablet|Touch/i.test(ua)) return "other_mobile";

  return "desktop";
};

const MENU_PREVIEWS = {
  notes: {
    title: "My Notes",
    tagline: "AI-Powered Smart UPSC Revision Notebook",
    icon: "/assets/images/my_notes_new_sidebar.svg",
    accent: "#6366F1",
    accentBg: "#EEF2FF",
    stats: [
      { label: "Active Notes", val: "24 Saved" },
      { label: "AI Summaries", val: "100% Synced" },
      { label: "Syllabus Tags", val: "GS 1-4 & Opt" },
    ],
    features: [
      {
        title: "One-Click AI Note Generation",
        desc: "Convert chat discussions, editorial breakdowns, and model answers directly into organized study notes.",
      },
      {
        title: "Syllabus Tagging & Subject Filtering",
        desc: "Categorize by GS Papers (GS-1, 2, 3, 4), Optional, and Prelims with search and instant tag filters.",
      },
      {
        title: "Key Takeaways & High-Yield Bullets",
        desc: "Automatic extraction of critical facts, committee names, Supreme Court judgments, and statutory bodies.",
      },
    ],
    cards: [
      {
        tag: "GS-3 · Economy",
        tagColor: "#059669",
        tagBg: "#ECFDF5",
        title: "Monetary Policy Transmission & RBI MPC Decisions",
        snippet:
          "Key repo rate levers, liquidity management via Standing Deposit Facility (SDF), and impact on inflation targeting framework...",
        date: "Updated Today",
      },
      {
        tag: "GS-2 · Polity",
        tagColor: "#2563EB",
        tagBg: "#EFF6FF",
        title: "Federalism & Governor's Discretionary Powers (Art 200)",
        snippet:
          "S.R. Bommai principles, Shamsher Singh benchmark, Punchhi Commission recommendations on assent timelines...",
        date: "2 days ago",
      },
    ],
  },
  tests: {
    title: "My Tests",
    tagline: "Adaptive Prelims & Mains UPSC Test Series",
    icon: "/assets/images/Prelim_test_new_sidebat.svg",
    accent: "#0284C7",
    accentBg: "#F0F9FF",
    stats: [
      { label: "Question Pool", val: "15,000+ MCQs" },
      { label: "Evaluation", val: "Instant + AI" },
      { label: "Test Types", val: "Sectional & FLT" },
    ],
    features: [
      {
        title: "UPSC Standard MCQ Practice",
        desc: "Pair matching, assertion-reasoning, and multi-statement questions meticulously mapped to past 10-year patterns.",
      },
      {
        title: "AI Detailed Explanations",
        desc: "In-depth rationale for both correct and incorrect options, concept traps, and NCERT/Standard book citations.",
      },
      {
        title: "Speed & Accuracy Analytics",
        desc: "Track elimination efficiency, time spent per question, and subject-wise accuracy benchmarks.",
      },
    ],
    cards: [
      {
        tag: "Sectional Test · GS-1",
        tagColor: "#D97706",
        tagBg: "#FFFBEB",
        title: "Modern History: 1857 Revolt & Nationalist Movements",
        snippet:
          "25 MCQs · 30 Mins · Moderate Difficulty · Detailed AI solution keys and conceptual traps analysis included.",
        date: "Score: 42/50 · 84%",
      },
      {
        tag: "Full Length Mock · CSAT",
        tagColor: "#7C3AED",
        tagBg: "#F5F3FF",
        title: "UPSC Prelims Mock FLT - Reading Comprehension & LR",
        snippet:
          "80 Questions · 200 Marks · Standard negative marking calibration (-0.66) with percentile ranking.",
        date: "Available in Pro",
      },
    ],
  },
  syllabus: {
    title: "Interactive Syllabus",
    tagline: "Micro-Topic UPSC Syllabus Navigator & Tracker",
    icon: "/assets/images/interactive_new_sidebar.svg",
    accent: "#10B981",
    accentBg: "#ECFDF5",
    stats: [
      { label: "Topic Granularity", val: "1,200+ Nodes" },
      { label: "Coverage", val: "Prelims + Mains" },
      { label: "PYQ Integration", val: "10-Yr Trends" },
    ],
    features: [
      {
        title: "Micro-Thematic Decomposition",
        desc: "Every broad UPSC syllabus line broken down into 10–15 bite-sized micro-topics with learning milestones.",
      },
      {
        title: "Direct PYQ & Resource Linking",
        desc: "Click any micro-topic to view exact past Prelims questions and Mains prompts asked over the last decade.",
      },
      {
        title: "Smart Progress Tracker",
        desc: "Mark topics as Mastered, In Progress, or For Revision, with visual progress bars across GS-1 through GS-4.",
      },
    ],
    cards: [
      {
        tag: "GS-2 · Governance",
        tagColor: "#0284C7",
        tagBg: "#F0F9FF",
        title: "Statutory, Regulatory & Quasi-Judicial Bodies",
        snippet:
          "8 Micro-topics: NHRC, CCI, NGT, TRAI, SEBI · 14 Past Mains questions linked · 85% topic syllabus coverage.",
        date: "3/8 Completed",
      },
      {
        tag: "GS-3 · Environment",
        tagColor: "#059669",
        tagBg: "#ECFDF5",
        title: "Conservation, Environmental Pollution & Degradation, EIA",
        snippet:
          "12 Micro-topics: Wetland norms, COP commitments, Carbon credit trading framework, EIA 2020 notification...",
        date: "9/12 Completed",
      },
    ],
  },
  planner: {
    title: "Study Planner",
    tagline: "Target-Driven Daily & Weekly Study Timetable",
    icon: "/assets/images/study_planner_new_sidebar.svg",
    accent: "#F59E0B",
    accentBg: "#FFFBEB",
    stats: [
      { label: "Target Year", val: "CSE 2027/28" },
      { label: "Plan Pacing", val: "Dynamic & Adaptive" },
      { label: "Revision Cycles", val: "3-Tier Spaced" },
    ],
    features: [
      {
        title: "Personalized Preparation Milestones",
        desc: "Tailored schedules for Beginners, Repeaters, or Working Professionals based on daily available study hours.",
      },
      {
        title: "3-Tier Spaced Revision Built-In",
        desc: "Automated prompts for 24-hour, 7-day, and 30-day revision cycles to guarantee long-term static retention.",
      },
      {
        title: "Dynamic Backlog Adjustment",
        desc: "Missed a session? The planner recalculates your roadmap without letting pending tasks accumulate.",
      },
    ],
    cards: [
      {
        tag: "Daily Target · Today",
        tagColor: "#EA580C",
        tagBg: "#FFF7ED",
        title: "Polity: Fundamental Rights (Art 12–35) + 30 MCQs",
        snippet:
          "Estimated 3.5 hrs · Focus on Writ Jurisdiction & Reasonable Restrictions · Includes 15-min flash revision.",
        date: "Target: 4.5 hrs total",
      },
      {
        tag: "Weekly Milestone · Week 14",
        tagColor: "#6366F1",
        tagBg: "#EEF2FF",
        title: "Finish Indian Economy Static (Macro) + 1 GS-3 Answer",
        snippet:
          "Balance: Fiscal deficit targets, FRBM Act roadmap, Banking NPA trends and Insolvency & Bankruptcy Code (IBC).",
        date: "Progress: 68%",
      },
    ],
  },
};

const TEMPLATE_CARDS = [
  {
    id: "current_affairs",
    title: "Current Affairs",
    //     badge: "Preview",
    subtitle: "Today's editorial analysis, UPSC syllabus mapping & key briefs",
    prompt: "",
    isCurrentAffairs: true,
    colSpan: 9,
    image: "/assets/images/trending-topics.svg",
    bgGradient: "linear-gradient(135deg, #FFF7ED 0%, #FFEDD5 100%)",
    accentColor: "#EA580C",
    icon: <Newspaper size={15} color="#EA580C" />,
  },
  {
    id: "short_notes",
    title: "Quick Topic Summary",
    subtitle: "Concise revision bullets for static syllabus",
    prompt:
      "Give me a quick 5-minute revision summary of Plate Tectonics and major seismic zones.",
    colSpan: 7,
    image: "/assets/images/short-notes.svg",
    bgGradient: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)",
    accentColor: "#059669",
    icon: <Calendar size={15} color="#0284C7" />,
  },
  {
    id: "case_study",
    title: "Explore a Case Study",
    subtitle: "GS-4 Ethics dilemma & stakeholder mapping",
    prompt:
      "Explore an ethics case study on public official conflict of interest in disaster relief procurement.",
    colSpan: 8,
    image: "/assets/images/cas-study2.svg",
    bgGradient: "linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)",
    accentColor: "#D97706",
    icon: <FileText size={15} color="#D97706" />,
  },
  {
    id: "model_answer",
    title: "See a Model Answer",
    subtitle: "High-scoring GS framework & evaluation",
    prompt:
      "Show me a model answer for: 'Discuss the impact of climate change on Indian agriculture and policy measures needed.'",
    colSpan: 7,
    image: "/assets/images/model-ans.svg",
    bgGradient: "linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)",
    accentColor: "#0284C7",
    icon: <Edit3 size={15} color="#7C3AED" />,
  },
  {
    id: "frame_essay",
    title: "Frame Your Essay",
    subtitle: "Introduction, philosophical angles & conclusion",
    prompt:
      "Frame a detailed essay outline for: 'The ship in harbor is safe, but that is not what ships are built for.'",
    colSpan: 6,
    image: "/assets/images/eassy-guidance-new.svg",
    bgGradient: "linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)",
    accentColor: "#BE185D",
    icon: <BookOpen size={15} color="#0D9488" />,
  },
  {
    id: "pyq_prelims",
    title: "Prelims Rapid Fire",
    subtitle: "Conceptual elimination techniques",
    prompt:
      "Test me with 3 high-yield Prelims questions on Biodiversity Hotspots and National Parks.",
    colSpan: 5,
    image: "/assets/images/info.svg",
    bgGradient: "linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)",
    accentColor: "#16A34A",
    icon: <BookOpen size={15} color="#16A34A" />,
  },
  {
    id: "pyq_mains",
    title: "PYQ Mains Trends",
    subtitle: "Past 10-year recurring thematic patterns",
    prompt:
      "Analyze recurring PYQ Mains questions on Industrial Growth and MSME manufacturing in India.",
    colSpan: 6,
    image: "/assets/images/model-ans.svg",
    bgGradient: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)",
    accentColor: "#7C3AED",
    icon: <Edit3 size={15} color="#7C3AED" />,
  },
];

const getPreferredNameError = (value) => {
  if (value === undefined || value === null || !value.trim()) {
    return "Preferred name cannot be empty or blank space.";
  }

  const trimmed = value.trim();

  // Check for emojis
  const emojiRegex =
    /(\p{Extended_Pictographic}|\p{Emoji_Presentation}|[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{27BF}])/u;
  if (emojiRegex.test(value)) {
    return "Emojis are not allowed in preferred name.";
  }

  // Check for Unicode or ASCII escape codes / HTML entities (e.g., \u0041, &#65;, %20)
  if (
    /\\u[0-9a-fA-F]{4}|&#[0-9]+;|&#x[0-9a-fA-F]+;|%[0-9a-fA-F]{2}/i.test(value)
  ) {
    return "Unicode or ASCII codes are not allowed.";
  }

  // Check for control ASCII characters
  const hasControlChars = [...value].some((ch) => {
    const code = ch.charCodeAt(0);
    return (code >= 0 && code <= 31) || code === 127;
  });
  if (hasControlChars) {
    return "Control characters are not allowed.";
  }

  // Check for only numbers
  if (/^\d+$/.test(trimmed)) {
    return "Preferred name cannot consist of only numbers.";
  }

  // Check for starting with numbers
  if (/^\d/.test(trimmed)) {
    return "Preferred name cannot start with a number.";
  }

  // Check for numbers anywhere
  if (/\d/.test(trimmed)) {
    return "Preferred name cannot contain numbers.";
  }

  // Check for special ASCII symbols / special characters
  if (/[^a-zA-Z\s'-]/.test(trimmed)) {
    return "Preferred name should only contain letters.";
  }

  if (trimmed.length < 2) {
    return "Preferred name must be at least 2 characters long.";
  }

  if (trimmed.length > 50) {
    return "Preferred name cannot exceed 50 characters.";
  }

  return "";
};

const Homepage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  // UI state: on desktop, collapsed false (210px expanded) or true (56px collapsed)
  const [isSidebarColl, setIsSidebarColl] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("");
  const [isMobile, setIsMobile] = useState(() => {
    return typeof window !== "undefined" ? window.innerWidth < 768 : false;
  });
  const [deviceType, setDeviceType] = useState(() => getDeviceType());

  useEffect(() => {
    const checkDevice = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setMobileDrawerOpen(false);

      const detected = getDeviceType();
      setDeviceType(detected);
      if (detected !== "desktop") {
        try {
          const params = new URLSearchParams(window.location.search);
          if (params.get("device") || sessionStorage.getItem("yooki_device_notice_dismissed") !== "true") {
            setShowDeviceNotice(true);
          }
        } catch (e) {
          setShowDeviceNotice(true);
        }
      }
    };
    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [canDismissLogin, setCanDismissLogin] = useState(true);
  const [showDeviceNotice, setShowDeviceNotice] = useState(() => {
    if (typeof window === "undefined") return false;
    const initialDevice = getDeviceType();
    if (initialDevice === "desktop") return false;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("device")) return true;
    } catch (e) {}
    return sessionStorage.getItem("yooki_device_notice_dismissed") !== "true";
  });

  const handleNewChat = () => {
    setActiveMenu("");
    if (isMobile) setMobileDrawerOpen(false);
    navigate("/");
  };

  // Policy Modal state ('tnc' | 'privacy')
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [policyModalType, setPolicyModalType] = useState("tnc");

  // Conversational onboarding state
  const [preferredName, setPreferredName] = useState(
    () => localStorage.getItem("yooki_guest_name") || "",
  );
  const [nameInput, setNameInput] = useState("");
  const [nameError, setNameError] = useState("");
  const [isNameInputFocused, setIsNameInputFocused] = useState(false);
  const [step, setStep] = useState(() => {
    if (localStorage.getItem("yooki_guest_18_verified") === "true")
      return "active";
    return "name_prompt"; // 'name_prompt' | 'age_modal' | 'under18' | 'active'
  });

  // Age modal radio
  const [ageSelection, setAgeSelection] = useState("");

  // Chat conversation
  const [limitReached, setLimitReached] = useState(false);
  const [messages, setMessages] = useState([]);
  const [queryInput, setQueryInput] = useState("");
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [isAiResponding, setIsAiResponding] = useState(false);
  const [conversationId, setConversationId] = useState("");
  const messagesEndRef = useRef(null);

  const answerTypeOptions = useMemo(
    () => ["model_answer", "frame_essay", "short_notes", "case_study"],
    [],
  );

  const promptOptions = useMemo(() => {
    if (messages.length === 0) return TEMPLATE_CARDS;
    return answerTypeOptions
      .map((id) => TEMPLATE_CARDS.find((card) => card.id === id))
      .filter(Boolean);
  }, [answerTypeOptions, messages.length]);

  // Current affairs preview modal
  const [todayCaModal, setTodayCaModal] = useState(false);
  const [todayArticles, setTodayArticles] = useState([]);
  const [loadingCa, setLoadingCa] = useState(false);

  // Feature preview modal for sidebar items (notes, tests, syllabus, planner)
  const [previewModalKey, setPreviewModalKey] = useState(null);

  // Bento box cards: maintain exact user-defined order
  const bentoCards = useMemo(() => {
    return TEMPLATE_CARDS;
  }, []);

  const isLoginOnlyPrompt =
    selectedPrompt?.id === "pyq_prelims" ||
    selectedPrompt?.id === "pyq_mains";

  const isSendActive = useMemo(() => {
    if (limitReached || isAiResponding) return false;
    const alphabetCount = (queryInput.match(/[a-zA-Z\p{L}]/gu) || []).length;
    return alphabetCount >= 3;
  }, [limitReached, isAiResponding, queryInput]);

  // Festive Theme State (db-driven via mtr_promotions theme or ?theme= query param)
  const [festiveTheme, setFestiveTheme] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("theme") || null;
  });
  const [festiveHeaderArt, setFestiveHeaderArt] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const loadTheme = async () => {
      try {
        const res = await fetchActiveTheme();
        if (isMounted && res?.success) {
          const params = new URLSearchParams(window.location.search);
          const themeToApply = params.get("theme") || res.theme || null;
          if (themeToApply) {
            setFestiveTheme(themeToApply);
          }

          // Candidate banner art: DB image_url or default blob asset
          const candidateUrl =
            res.image_url ||
            (themeToApply
              ? `${FESTIVE_ASSET_BASE}/${normalizeTheme(themeToApply)}.jpg`
              : null);

          if (candidateUrl) {
            // Pre-load guard: only display banner if image successfully loads from storage
            const testImg = new Image();
            testImg.onload = () => {
              if (isMounted) setFestiveHeaderArt(candidateUrl);
            };
            testImg.onerror = () => {
              // 404 or missing blob: gracefully hide banner and leave header clean
              if (isMounted) setFestiveHeaderArt(null);
            };
            testImg.src = candidateUrl;
          } else {
            if (isMounted) setFestiveHeaderArt(null);
          }
        }
      } catch (err) {
        console.warn("Could not load active seasonal theme:", err);
        if (isMounted) setFestiveHeaderArt(null);
      }
    };
    loadTheme();
    return () => {
      isMounted = false;
    };
  }, []);

  // Initialize session on mount if verified
  useEffect(() => {
    if (step === "active" && preferredName) {
      initGuestSession(preferredName, true);
    }
  }, [step, preferredName]);

  // Name submission handler
  const handleNameChange = (e) => {
    const val = e.target.value;
    setNameInput(val);
    if (nameError) {
      setNameError(getPreferredNameError(val));
    }
  };

  const handleNameSubmit = (e) => {
    e?.preventDefault();
    const error = getPreferredNameError(nameInput);
    if (error) {
      setNameError(error);
      return;
    }
    setNameError("");
    const clean = nameInput.trim().replace(/\s+/g, " ");
    setPreferredName(clean);
    localStorage.setItem("yooki_guest_name", clean);
    setStep("age_modal");
  };

  // Age verification continue handler
  const handleAgeContinue = async () => {
    if (ageSelection === "under_18") {
      setStep("under18");
      return;
    }
    if (ageSelection === "18_plus") {
      localStorage.setItem("yooki_guest_18_verified", "true");
      setStep("active");
      await initGuestSession(preferredName, true);
    }
  };

  const handleSelectTemplate = (card) => {
    if (!card) return;
    if (card.isCurrentAffairs) {
      handleOpenTodayCa();
      return;
    }
    if (selectedPrompt?.id === card.id) {
      setSelectedPrompt(null);
      return;
    }
    setSelectedPrompt({
      id: card.id,
      title: card.title,
      prompt: card.prompt || "",
    });
  };

  const handleClearSelectedPrompt = () => {
    setSelectedPrompt(null);
  };

  // Send message / template prompt
  const handleSendMessage = async (textToSend) => {
    const rawAnswerType = selectedPrompt?.id || "generic";
    const answerTypeMap = {
      model_answer: "answer",
      frame_essay: "essay", // also covers easy
      easy: "essay",
      short_notes: "short_note",
      case_study: "case_study",
    };
    const answerType =
      answerTypeMap[rawAnswerType] || rawAnswerType || "generic";
    const text = (
      textToSend ||
      queryInput ||
      selectedPrompt?.prompt ||
      ""
    ).trim();
    if (!text || isAiResponding) return;

    if (
      selectedPrompt?.id === "pyq_prelims" ||
      selectedPrompt?.id === "pyq_mains"
    ) {
      setCanDismissLogin(true);
      setLoginModalOpen(true);
      return;
    }

    if (!isSendActive && !textToSend) return;

    if (limitReached) {
      setCanDismissLogin(false);
      setLoginModalOpen(true);
      return;
    }

    setSelectedPrompt(null);
    setQueryInput("");
    const newMessages = [...messages, { role: "user", content: text }];
    setMessages(newMessages);
    setIsAiResponding(true);

    try {
      const response = await sendDemoMentorMind({
        user_id: localStorage.getItem("user_id") || getOrCreateGuestUuid(),
        prompt: text,
        conversation_id: conversationId,
        answer_type: answerType,
      });
      if (!response?.message) {
        const detail = response?.detail || "Empty response from Mentormind";
        if (/free credits exhausted|sign in/i.test(detail)) {
          setLimitReached(true);
          setCanDismissLogin(false);
          setLoginModalOpen(true);
          return;
        }
        throw new Error(detail);
      }
      setMessages([
        ...newMessages,
        { role: "assistant", content: response.message },
      ]);
      if (response.conversation_id) {
        setConversationId(response.conversation_id);
      }
    } catch (err) {
      const detail = err?.data?.detail || err?.detail || err?.message || "";
      if (/free credits exhausted|sign in/i.test(detail)) {
        setLimitReached(true);
        setCanDismissLogin(false);
        setLoginModalOpen(true);
        return;
      }
      console.error("Guest chat error:", err);
      setMessages([
        ...newMessages,
        {
          role: "assistant",
          content:
            "Sorry, I encountered a brief issue while generating your response. Please try again or log in for uninterrupted guidance.",
        },
      ]);
    } finally {
      setIsAiResponding(false);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [messages, isAiResponding]);

  // Open Today's Current Affairs
  const handleOpenTodayCa = async () => {
    setTodayCaModal(true);
    setLoadingCa(true);
    const articles = await getTodayCurrentAffairs();
    setTodayArticles(articles);
    setLoadingCa(false);
  };

  return (
    <div
      style={{
        display: "flex",
        width: "100vw",
        minWidth: "100%",
        height: "100vh",
        backgroundColor: "#FFFFFF",
        fontFamily:
          "'Source Sans 3', -apple-system, BlinkMacSystemFont, sans-serif",
        overflow: "hidden",
        position: "relative",
        boxSizing: "border-box",
      }}
    >
      {/* ──────────────── Left Sidebar (Desktop: 210px Expanded / 56px Collapsed via Sidebar.css) ──────────────── */}
      {!isMobile && (
        <div
          className="sidebar-wrapper"
          data-collapsed={isSidebarColl}
          style={{
            position: "relative",
            height: "100vh",
            display: "flex",
            flexShrink: 0,
          }}
        >
          <button
            className="sidebar-floating-toggle"
            onClick={() => setIsSidebarColl((prev) => !prev)}
            aria-label={isSidebarColl ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isSidebarColl ? "›" : "‹"}
          </button>

          <div
            className={`sidebar sidebar-shell ${isSidebarColl ? "Iside isvisible collapsed" : "Notiside"}`}
            data-sidebar-state={isSidebarColl ? "collapsed" : "expanded"}
            style={{
              height: "100vh",
              backgroundColor: "#FFFFFF",
              borderRight: "1px solid #EAECEF",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              boxSizing: "border-box",
              overflow: isSidebarColl ? "visible" : "hidden",
            }}
          >
            {/* Top Logo */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: isSidebarColl ? "center" : "flex-start",
                padding: isSidebarColl ? "6px 0 14px 0" : "6px 6px 14px 6px",
                minHeight: "44px",
                boxSizing: "border-box",
                cursor: "pointer",
              }}
              onClick={handleNewChat}
              title="Yooki"
            >
              {isSidebarColl ? (
                <img
                  src="/assets/images/logo/Favicon/1X/Favicon.svg"
                  alt="Yooki"
                  style={{
                    width: "28px",
                    height: "28px",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <img
                  src="/assets/images/logo/SVG/Logo-Org.svg"
                  alt="Yooki"
                  style={{
                    height: "28px",
                    width: "auto",
                    objectFit: "contain",
                  }}
                />
              )}
            </div>

            {/* Menu Items List */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                overflowX: isSidebarColl ? "visible" : "hidden",
                display: "flex",
                flexDirection: "column",
                gap: "2px",
              }}
            >
              {/* Spacer preserving vertical slot of New Chat without moving other items */}
              <div
                style={{
                  height: "36px",
                  flexShrink: 0,
                }}
                aria-hidden="true"
              />

              {/* 2. Current Affairs */}
              <div
                className={`nav-link sidebar-menu-item ${activeMenu === "currentAffairs" ? "bg" : ""}`}
                onClick={() => {
                  setActiveMenu("currentAffairs");
                  handleOpenTodayCa();
                }}
                title="Current Affairs"
              >
                <div className="sidebar-menu-content">
                  <img
                    src="/assets/images/menu_icon_currentaffair.svg"
                    alt="Current Affairs"
                    className="sidebar-menu-icon"
                  />
                  <span className="open-sidebar-text">Current Affairs</span>
                </div>
              </div>

              {/* 3. My Notes */}
              <div
                className={`nav-link sidebar-menu-item ${activeMenu === "notes" ? "bg" : ""}`}
                onClick={() => {
                  setActiveMenu("notes");
                  setPreviewModalKey("notes");
                }}
                title="My Notes"
              >
                <div className="sidebar-menu-content">
                  <img
                    src="/assets/images/my_notes_new_sidebar.svg"
                    alt="My Notes"
                    className="sidebar-menu-icon"
                  />
                  <span className="open-sidebar-text">My Notes</span>
                  {!isSidebarColl && (
                    <img
                      src="/assets/images/pro-button-gradient-1.svg"
                      alt="Pro"
                      style={{
                        height: "18px",
                        width: "auto",
                        marginLeft: "auto",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </div>
              </div>

              {/* 4. My Tests */}
              <div
                className={`nav-link sidebar-menu-item ${activeMenu === "tests" ? "bg" : ""}`}
                onClick={() => {
                  setActiveMenu("tests");
                  setPreviewModalKey("tests");
                }}
                title="My Tests"
              >
                <div className="sidebar-menu-content">
                  <img
                    src="/assets/images/Prelim_test_new_sidebat.svg"
                    alt="My Tests"
                    className="sidebar-menu-icon"
                  />
                  <span className="open-sidebar-text">My Tests</span>
                  {!isSidebarColl && (
                    <img
                      src="/assets/images/pro-button-gradient-1.svg"
                      alt="Pro"
                      style={{
                        height: "18px",
                        width: "auto",
                        marginLeft: "auto",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </div>
              </div>

              {/* 5. Interactive Syllabus */}
              <div
                className={`nav-link sidebar-menu-item ${activeMenu === "syllabus" ? "bg" : ""}`}
                onClick={() => {
                  setActiveMenu("syllabus");
                  setPreviewModalKey("syllabus");
                }}
                title="Interactive Syllabus"
              >
                <div className="sidebar-menu-content">
                  <img
                    src="/assets/images/interactive_new_sidebar.svg"
                    alt="Interactive Syllabus"
                    className="sidebar-menu-icon"
                  />
                  <span className="open-sidebar-text">
                    Interactive Syllabus
                  </span>
                  {!isSidebarColl && (
                    <img
                      src="/assets/images/pro-button-gradient-1.svg"
                      alt="Pro"
                      style={{
                        height: "18px",
                        width: "auto",
                        marginLeft: "auto",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </div>
              </div>

              {/* 6. Study Planner */}
              <div
                className={`nav-link sidebar-menu-item ${activeMenu === "planner" ? "bg" : ""}`}
                onClick={() => {
                  setActiveMenu("planner");
                  setPreviewModalKey("planner");
                }}
                title="Study Planner"
              >
                <div className="sidebar-menu-content">
                  <img
                    src="/assets/images/study_planner_new_sidebar.svg"
                    alt="Study Planner"
                    className="sidebar-menu-icon"
                  />
                  <span className="open-sidebar-text">Study Planner</span>
                  {!isSidebarColl && (
                    <img
                      src="/assets/images/pro-button-gradient-1.svg"
                      alt="Pro"
                      style={{
                        height: "18px",
                        width: "auto",
                        marginLeft: "auto",
                        flexShrink: 0,
                      }}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Bottom Conversion Section */}
            <div
              style={{
                marginTop: "auto",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {/* About Yooki with right-aligned ExternalLink icon matching reference */}
              <div
                className={isSidebarColl ? "nav-link sidebar-menu-item" : ""}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: isSidebarColl ? "center" : "space-between",
                  width: isSidebarColl ? "36px" : "100%",
                  height: isSidebarColl ? "34px" : "auto",
                  margin: isSidebarColl ? "0 auto 6px auto" : "0 0 6px 0",
                  padding: isSidebarColl ? 0 : "8px 12px",
                  cursor: "pointer",
                  borderRadius: isSidebarColl ? "8px" : "6px",
                  boxSizing: "border-box",
                  transition: "background-color 0.15s ease",
                }}
                onClick={() => window.open("/about.html", "_blank")}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#F1F5F9";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
                title="About Yooki"
              >
                {!isSidebarColl ? (
                  <>
                    <span
                      style={{
                        fontFamily: "'Source Sans 3', sans-serif",
                        fontSize: "13.5px",
                        fontWeight: 600,
                        color: "#334155",
                      }}
                    >
                      About Yooki
                    </span>
                    <ExternalLink
                      size={15}
                      style={{
                        color: "#94A3B8",
                        strokeWidth: 1.75,
                        flexShrink: 0,
                      }}
                    />
                  </>
                ) : (
                  <ExternalLink
                    size={16}
                    style={{ color: "#94A3B8", strokeWidth: 1.75 }}
                  />
                )}
              </div>

              {/* Get App */}
              <div
                className={isSidebarColl ? "nav-link sidebar-menu-item" : ""}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: isSidebarColl ? "center" : "space-between",
                  width: isSidebarColl ? "36px" : "100%",
                  height: isSidebarColl ? "34px" : "auto",
                  margin: isSidebarColl ? "0 auto 8px auto" : "0 0 8px 0",
                  padding: isSidebarColl ? 0 : "8px 12px",
                  cursor: "pointer",
                  borderRadius: isSidebarColl ? "8px" : "6px",
                  boxSizing: "border-box",
                  transition: "background-color 0.15s ease",
                }}
                onClick={() =>
                  window.open(
                    "https://yookistorage.blob.core.windows.net/assets/askyooki.apk",
                    "_blank",
                  )
                }
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#F1F5F9";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
                title="Get App"
              >
                {!isSidebarColl ? (
                  <>
                    <span
                      style={{
                        fontFamily: "'Source Sans 3', sans-serif",
                        fontSize: "13.5px",
                        fontWeight: 600,
                        color: "#334155",
                      }}
                    >
                      Get the Yooki App
                    </span>
                    <Download
                      size={15}
                      style={{
                        color: "#94A3B8",
                        strokeWidth: 1.75,
                        flexShrink: 0,
                      }}
                    />
                  </>
                ) : (
                  <Download
                    size={16}
                    style={{ color: "#94A3B8", strokeWidth: 1.75 }}
                  />
                )}
              </div>

              {!isSidebarColl ? (
                <div
                  style={{
                    padding: "12px 10px",
                    borderTop: "1px solid #EAECEF",
                    backgroundColor: "#F8FAFC",
                    borderRadius: "8px",
                    margin: "4px 0",
                  }}
                >
                  <h4
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      color: "#1E293B",
                      margin: "0 0 4px 0",
                    }}
                  >
                    Get UPSC-focused responses
                  </h4>
                  <p
                    style={{
                      fontSize: "11px",
                      color: "#64748B",
                      margin: "0 0 10px 0",
                      lineHeight: "1.3",
                    }}
                  >
                    Log in to save your chat history beyond 60 days.
                  </p>
                  <button
                    onClick={() => {
                      setCanDismissLogin(true);
                      setLoginModalOpen(true);
                    }}
                    style={{
                      width: "100%",
                      height: "34px",
                      backgroundColor: "#2BA9D4",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: "6px",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Log in
                  </button>
                </div>
              ) : (
                <div
                  className="nav-link sidebar-menu-item"
                  onClick={() => {
                    setCanDismissLogin(true);
                    setLoginModalOpen(true);
                  }}
                  title="Log in"
                  style={{
                    margin: "6px auto",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <div className="sidebar-menu-content">
                    <LogIn
                      size={20}
                      color="#2BA9D4"
                      className="sidebar-menu-icon"
                    />
                    <span className="open-sidebar-text">Log in</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ──────────────── Mobile Slide-out Drawer (Overlay) ──────────────── */}
      {isMobile && mobileDrawerOpen && (
        <>
          <div
            onClick={() => setMobileDrawerOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(15, 23, 42, 0.45)",
              backdropFilter: "blur(2px)",
              zIndex: 998,
            }}
          />
          <aside
            style={{
              position: "fixed",
              top: 0,
              bottom: 0,
              left: 0,
              width: "250px",
              backgroundColor: "#FFFFFF",
              zIndex: 999,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              boxShadow: "4px 0 24px rgba(0,0,0,0.15)",
              padding: "16px 14px",
              boxSizing: "border-box",
            }}
          >
            {/* Header: Logo + Close */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "16px",
              }}
            >
              <img
                src="/assets/images/logo/SVG/Logo-Org.svg"
                alt="Yooki"
                style={{ height: "30px", width: "auto" }}
                onClick={() => {
                  setMobileDrawerOpen(false);
                  navigate("/");
                }}
              />
              <button
                onClick={() => setMobileDrawerOpen(false)}
                style={{
                  background: "#F1F5F9",
                  border: "none",
                  borderRadius: "8px",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#64748B",
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Menu items */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              }}
            >
              {/* Spacer preserving vertical slot of New Chat */}
              <div style={{ height: "36px", flexShrink: 0 }} aria-hidden="true" />

              {/* 2. Current Affairs */}
              <div
                className="nav-link sidebar-menu-item"
                onClick={() => {
                  setMobileDrawerOpen(false);
                  handleOpenTodayCa();
                }}
              >
                <div className="sidebar-menu-content">
                  <img
                    src="/assets/images/menu_icon_currentaffair.svg"
                    alt="Current Affairs"
                    className="sidebar-menu-icon"
                  />
                  <span className="open-sidebar-text">Current Affairs</span>
                </div>
              </div>

              {/* 3. My Notes */}
              <div
                className="nav-link sidebar-menu-item"
                onClick={() => {
                  setMobileDrawerOpen(false);
                  setPreviewModalKey("notes");
                }}
              >
                <div className="sidebar-menu-content">
                  <img
                    src="/assets/images/my_notes_new_sidebar.svg"
                    alt="My Notes"
                    className="sidebar-menu-icon"
                  />
                  <span className="open-sidebar-text">My Notes</span>
                  <img
                    src="/assets/images/pro-button-gradient-1.svg"
                    alt="Pro"
                    style={{
                      height: "18px",
                      width: "auto",
                      marginLeft: "auto",
                      flexShrink: 0,
                    }}
                  />
                </div>
              </div>

              {/* 4. My Tests */}
              <div
                className="nav-link sidebar-menu-item"
                onClick={() => {
                  setMobileDrawerOpen(false);
                  setPreviewModalKey("tests");
                }}
              >
                <div className="sidebar-menu-content">
                  <img
                    src="/assets/images/Prelim_test_new_sidebat.svg"
                    alt="My Tests"
                    className="sidebar-menu-icon"
                  />
                  <span className="open-sidebar-text">My Tests</span>
                  <img
                    src="/assets/images/pro-button-gradient-1.svg"
                    alt="Pro"
                    style={{
                      height: "18px",
                      width: "auto",
                      marginLeft: "auto",
                      flexShrink: 0,
                    }}
                  />
                </div>
              </div>

              {/* 5. Interactive Syllabus */}
              <div
                className="nav-link sidebar-menu-item"
                onClick={() => {
                  setMobileDrawerOpen(false);
                  setPreviewModalKey("syllabus");
                }}
              >
                <div className="sidebar-menu-content">
                  <img
                    src="/assets/images/interactive_new_sidebar.svg"
                    alt="Interactive Syllabus"
                    className="sidebar-menu-icon"
                  />
                  <span className="open-sidebar-text">
                    Interactive Syllabus
                  </span>
                  <img
                    src="/assets/images/pro-button-gradient-1.svg"
                    alt="Pro"
                    style={{
                      height: "18px",
                      width: "auto",
                      marginLeft: "auto",
                      flexShrink: 0,
                    }}
                  />
                </div>
              </div>

              {/* 6. Study Planner */}
              <div
                className="nav-link sidebar-menu-item"
                onClick={() => {
                  setMobileDrawerOpen(false);
                  setPreviewModalKey("planner");
                }}
              >
                <div className="sidebar-menu-content">
                  <img
                    src="/assets/images/study_planner_new_sidebar.svg"
                    alt="Study Planner"
                    className="sidebar-menu-icon"
                  />
                  <span className="open-sidebar-text">Study Planner</span>
                  <img
                    src="/assets/images/pro-button-gradient-1.svg"
                    alt="Pro"
                    style={{
                      height: "18px",
                      width: "auto",
                      marginLeft: "auto",
                      flexShrink: 0,
                    }}
                  />
                </div>
              </div>
            </div>

            {/* About Yooki with right-aligned ExternalLink icon */}
            <div
              style={{
                padding: "8px 12px",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                borderRadius: "6px",
              }}
              onClick={() => {
                setMobileDrawerOpen(false);
                window.open("/about.html", "_blank");
              }}
            >
              <span
                style={{
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: "13.5px",
                  fontWeight: 600,
                  color: "#334155",
                }}
              >
                About Yooki
              </span>
              <ExternalLink
                size={15}
                style={{ color: "#94A3B8", strokeWidth: 1.75, flexShrink: 0 }}
              />
            </div>

            {/* Get App */}
            <div
              style={{
                padding: "8px 12px",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                borderRadius: "6px",
              }}
              onClick={() => {
                setMobileDrawerOpen(false);
                window.open(
                  "https://yookistorage.blob.core.windows.net/assets/askyooki.apk",
                  "_blank",
                );
              }}
              title="Get App"
            >
              <span
                style={{
                  fontFamily: "'Source Sans 3', sans-serif",
                  fontSize: "13.5px",
                  fontWeight: 600,
                  color: "#334155",
                }}
              >
                Get the Yooki App
              </span>
              <Download
                size={15}
                style={{ color: "#94A3B8", strokeWidth: 1.75, flexShrink: 0 }}
              />
            </div>

            {/* Bottom Conversion Card */}
            <div
              style={{
                padding: "12px 10px",
                borderTop: "1px solid #EAECEF",
                backgroundColor: "#F8FAFC",
                borderRadius: "8px",
              }}
            >
              <h4
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: "#1E293B",
                  margin: "0 0 4px 0",
                }}
              >
                Get UPSC-focused responses
              </h4>
              <p
                style={{
                  fontSize: "11px",
                  color: "#64748B",
                  margin: "0 0 10px 0",
                  lineHeight: "1.3",
                }}
              >
                Log in to save your chat history beyond 60 days.
              </p>
              <button
                onClick={() => {
                  setMobileDrawerOpen(false);
                  setCanDismissLogin(true);
                  setLoginModalOpen(true);
                }}
                style={{
                  width: "100%",
                  height: "34px",
                  backgroundColor: "#2BA9D4",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Log in
              </button>
            </div>
          </aside>
        </>
      )}

      {/* ──────────────── Main Content Area ──────────────── */}
      <div
        className="yooki-home-main"
        style={{
          flex: 1,
          minWidth: 0,
          maxWidth: "none",
          margin: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          height: "100vh",
          overflow: "hidden",
          backgroundColor: "#FFFFFF",
          boxSizing: "border-box",
        }}
      >
        {/* Sleek Top Navbar (Reduced vertical height) */}
        <header
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 24px",
            minHeight: festiveHeaderArt && !isMobile ? "64px" : "52px",
            borderBottom: "1px solid #EAECEF",
            backgroundColor: "#FFFFFF",
            position: "sticky",
            top: 0,
            zIndex: 10,
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {festiveHeaderArt && (
            <div
              className="festive-header-banner"
              aria-hidden="true"
              style={{ backgroundImage: `url("${festiveHeaderArt}")` }}
            />
          )}

          {/* Left: Mobile Menu Toggle + Brand Logo on mobile */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              position: "relative",
              zIndex: 1,
            }}
          >
            {isMobile && (
              <button
                onClick={() => setMobileDrawerOpen(true)}
                title="Open Menu"
                style={{
                  background: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  cursor: "pointer",
                  padding: "6px 8px",
                  color: "#334155",
                  borderRadius: "8px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Menu size={18} />
              </button>
            )}

            {isMobile && (
              <img
                src="/assets/images/logo/SVG/Logo-Org.svg"
                alt="Yooki"
                style={{
                  height: "30px",
                  width: "auto",
                  cursor: "pointer",
                  display: "block",
                }}
                onClick={() => navigate("/")}
              />
            )}
          </div>

          {/* Right: Actions (No 'Sign up for free' button per request) */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginLeft: "auto",
              position: "relative",
              zIndex: 1,
            }}
          >
            {!isMobile && (
              <>
                <a
                  href="https://blog.askyooki.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    height: "34px",
                    padding: "0 14px",
                    borderRadius: "8px",
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    color: "#242E49",
                    fontSize: "13px",
                    fontWeight: 500,
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#F1F5F9")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#F8FAFC")
                  }
                >
                  Blog
                </a>
                <a
                  href="https://t.me/askyooki"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    height: "34px",
                    padding: "0 14px",
                    borderRadius: "8px",
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    color: "#242E49",
                    fontSize: "13px",
                    fontWeight: 500,
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#F1F5F9")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#F8FAFC")
                  }
                >
                  Join Community
                </a>
              </>
            )}

            <button
              onClick={() => {
                setCanDismissLogin(true);
                setLoginModalOpen(true);
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                height: "34px",
                padding: "0 18px",
                borderRadius: "8px",
                backgroundColor: "#2BA9D4",
                color: "#FFFFFF",
                border: "none",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
                boxShadow: "0 2px 6px rgba(43, 169, 212, 0.25)",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#2498BF")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#2BA9D4")
              }
            >
              Log in
            </button>
          </div>
        </header>

        {/* Device Compatibility Advisory Banner */}
        {showDeviceNotice && deviceType !== "desktop" && (
          <div
            style={{
              backgroundColor: "#FFFBEB",
              borderBottom: "1px solid #FDE68A",
              padding: "7px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              fontSize: "12.5px",
              color: "#92400E",
              zIndex: 9,
              flexShrink: 0,
              boxSizing: "border-box",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                textAlign: "center",
                lineHeight: "1.4",
              }}
            >
              <span style={{ fontSize: "14px" }}>💡</span>
              {deviceType === "android" ? (
                <span>
                  <strong>Best viewed on desktop browsers.</strong> On mobile?{" "}
                  <a
                    href="https://yookistorage.blob.core.windows.net/assets/askyooki.apk"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#92400E",
                      fontWeight: 700,
                      textDecoration: "underline",
                      textUnderlineOffset: "2px",
                    }}
                  >
                    Download the Yooki Android App
                  </a>
                  .
                </span>
              ) : (
                <span>
                  <strong>For the best interactive learning and testing experience,</strong> please use a desktop web browser.
                </span>
              )}
            </span>
            <button
              onClick={() => {
                setShowDeviceNotice(false);
                sessionStorage.setItem("yooki_device_notice_dismissed", "true");
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "2px",
                display: "inline-flex",
                alignItems: "center",
                color: "#B45309",
                marginLeft: "8px",
                flexShrink: 0,
              }}
              title="Dismiss"
              aria-label="Dismiss notice"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Expansive Conversational Canvas */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            maxWidth: "920px",
            width: "100%",
            margin: "0 auto",
            padding: "32px 20px 16px 20px",
            display: "flex",
            flexDirection: "column",
            justifyContent: step === "name_prompt" ? "center" : "flex-start",
            overflowY: "auto",
            overflowX: "hidden",
            boxSizing: "border-box",
          }}
        >
          {/* ── STEP 1: Name Onboarding Prompt ── */}
          {step === "name_prompt" && (
            <div
              style={{
                textAlign: "center",
                animation: "fadeIn 0.3s ease",
                padding: "10px 0",
              }}
            >
              {/* Replace AI image with yooki_navigator.svg */}
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "20px",
                }}
              >
                <img
                  src="/assets/images/logo/yooki_navigator.svg"
                  alt="Yooki Navigator"
                  style={{
                    width: "54px",
                    height: "54px",
                    objectFit: "contain",
                  }}
                />
              </div>

              <h1
                style={{
                  fontFamily:
                    "'Source Sans 3', -apple-system, BlinkMacSystemFont, sans-serif",
                  fontSize: "36px",
                  fontWeight: 700,
                  color: "#1E293B",
                  marginBottom: "10px",
                  letterSpacing: "-0.5px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: "9px",
                }}
              >
                <span>What should</span>
                <img
                  src="/assets/images/logo/SVG/Logo-Org.svg"
                  alt="Yooki"
                  style={{
                    height: "28px",
                    width: "auto",
                    position: "relative",
                    top: "-2px",
                    display: "inline-block",
                  }}
                />
                <span>call you?</span>
              </h1>

              <p
                style={{
                  fontSize: "15px",
                  color: "#64748B",
                  marginBottom: "36px",
                }}
              >
                Your AI companion for structured, stress-free UPSC preparation.
              </p>

              <form
                onSubmit={handleNameSubmit}
                style={{
                  maxWidth: "520px",
                  margin: "0 auto",
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#FFFFFF",
                  padding: "6px 8px 6px 20px",
                  borderRadius: "9999px",
                  boxShadow: nameError
                    ? "0 0 0 3px rgba(220, 38, 38, 0.15)"
                    : isNameInputFocused
                    ? "0 0 0 3px rgba(43, 169, 212, 0.15)"
                    : "0 4px 20px rgba(0, 0, 0, 0.06)",
                  border: nameError
                    ? "1.5px solid #DC2626"
                    : isNameInputFocused
                    ? "1.5px solid #2BA9D4"
                    : "1.5px solid #E2E8F0",
                  transition: "border 0.2s, box-shadow 0.2s",
                }}
              >
                <input
                  type="text"
                  placeholder="Preferred name"
                  value={nameInput}
                  onChange={handleNameChange}
                  onFocus={() => setIsNameInputFocused(true)}
                  onBlur={() => setIsNameInputFocused(false)}
                  autoFocus
                  style={{
                    flex: 1,
                    border: "none",
                    outline: "none",
                    fontSize: "16px",
                    color: "#1E293B",
                    background: "transparent",
                  }}
                  aria-invalid={!!nameError}
                  aria-describedby={nameError ? "preferredNameError" : undefined}
                />
                <button
                  type="submit"
                  disabled={!nameInput.trim()}
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "50%",
                    backgroundColor: nameInput.trim() ? "#2BA9D4" : "#E2E8F0",
                    color: "#FFFFFF",
                    border: "none",
                    cursor: nameInput.trim() ? "pointer" : "default",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s",
                    flexShrink: 0,
                  }}
                >
                  <Send size={18} />
                </button>
              </form>
              {nameError && (
                <span
                  id="preferredNameError"
                  role="alert"
                  style={{
                    display: "block",
                    marginTop: "10px",
                    color: "#DC2626",
                    fontSize: "13px",
                    fontWeight: "500",
                    textAlign: "center",
                  }}
                >
                  {nameError}
                </span>
              )}
            </div>
          )}

          {/* ── STEP 2: Under 18 Refusal View ── */}
          {step === "under18" && (
            <div
              style={{
                textAlign: "center",
                maxWidth: "460px",
                margin: "auto",
                padding: "36px 24px",
                backgroundColor: "#FFFFFF",
                borderRadius: "16px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
                border: "1px solid #E2E8F0",
              }}
            >
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  margin: "0 auto 16px",
                  backgroundColor: "#FCE7F3",
                  color: "#BE185D",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Heart size={28} />
              </div>
              <h2
                style={{
                  fontSize: "22px",
                  fontWeight: 700,
                  color: "#1E293B",
                  marginBottom: "8px",
                }}
              >
                Yooki is intended for users 18 years old or over
              </h2>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748B",
                  lineHeight: "1.5",
                  marginBottom: "20px",
                }}
              >
                Sorry, people under 18 are not able to create an account or chat
                with Yooki at this time. The most important connections you can
                have are with teachers, parents, and friends.
              </p>
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#2BA9D4",
                  marginBottom: "24px",
                }}
              >
                See you when you turn 18!
              </p>
              <button
                onClick={() => setStep("name_prompt")}
                style={{
                  padding: "10px 24px",
                  backgroundColor: "#F1F5F9",
                  border: "1px solid #CBD5E1",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#334155",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          )}

          {/* ── STEP 3: Active Conversational State (Greeting + Template Cards + Chat) ── */}
          {step === "active" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                width: "100%",
              }}
            >
              {/* AI Initial Greeting Bubble */}
              <div
                style={{
                  backgroundColor: "#FFFFFF",
                  padding: "22px 26px",
                  borderRadius: "16px",
                  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.03)",
                  border: "1px solid #E2E8F0",
                  lineHeight: "1.6",
                }}
              >
                <p
                  style={{
                    fontSize: "19px",
                    fontWeight: 700,
                    color: "#1E293B",
                    margin: "0 0 6px 0",
                  }}
                >
                  Hey {preferredName} — got it! 😊
                </p>
                <p style={{ fontSize: "15px", color: "#475569", margin: 0 }}>
                  I'll keep it simple: you're here to prepare and succeed, and
                  I'm glad you are. What's on your mind today?
                </p>
              </div>

              {/* Chat Message History */}
              {messages.map((msg, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent:
                      msg.role === "user" ? "flex-end" : "flex-start",
                    marginBottom: "8px",
                  }}
                >
                  <div
                    style={{
                      maxWidth: msg.role === "user" ? "80%" : "100%",
                      width: msg.role === "user" ? undefined : "100%",
                      padding: msg.role === "user" ? "14px 18px" : "16px 20px",
                      borderRadius: "16px",
                      backgroundColor:
                        msg.role === "user" ? "#2BA9D4" : "#FFFFFF",
                      color: msg.role === "user" ? "#FFFFFF" : "#1E293B",
                      boxShadow:
                        msg.role === "user"
                          ? "0 2px 8px rgba(43, 169, 212, 0.2)"
                          : "0 2px 8px rgba(0, 0, 0, 0.04)",
                      border:
                        msg.role === "user" ? "none" : "1px solid #E2E8F0",
                      fontSize: "15px",
                      lineHeight: "1.6",
                      whiteSpace: msg.role === "user" ? "pre-wrap" : "normal",
                      boxSizing: "border-box",
                    }}
                  >
                    {msg.role === "user" ? (
                      msg.content
                    ) : (
                      <MentorMindResponse
                        content={msg.content}
                        onCardClick={handleSendMessage}
                        onLoginClick={() => {
                          setCanDismissLogin(true);
                          setLoginModalOpen(true);
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}

              {isAiResponding && (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "7px",
                    color: "#2BA9D4",
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: 1,
                    padding: "4px 0",
                  }}
                >
                  <img
                    src="/assets/images/logo/yooki_navigator.svg"
                    alt="Yooki"
                    style={{
                      height: "17px",
                      width: "auto",
                      display: "block",
                      flexShrink: 0,
                      transform: "translateY(-1.5px)",
                    }}
                  />
                  <span style={{ lineHeight: 1 }}>Yooki is thinking...</span>
                </div>
              )}

              <div ref={messagesEndRef} />

              {/* Initial Full Template Illustration Cards: 2-Row Modernistic Bento Box Layout (7 items) */}
              {messages.length === 0 && (
                <div style={{ marginTop: "14px", width: "100%" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "repeat(24, 1fr)",
                      gap: "12px",
                      width: "100%",
                    }}
                  >
                    {bentoCards.map((card) => {
                      return (
                        <div
                          key={card.id}
                          onClick={() => handleSelectTemplate(card)}
                          style={{
                            gridColumn: isMobile
                              ? "span 1"
                              : `span ${card.colSpan || 6}`,
                            background: card.bgGradient,
                            border: "1px solid rgba(0, 0, 0, 0.06)",
                            borderRadius: "14px",
                            padding: "14px 16px",
                            cursor: "pointer",
                            transition:
                              "all 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            minHeight: "88px",
                            boxSizing: "border-box",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-2px)";
                            e.currentTarget.style.boxShadow =
                              "0 8px 18px rgba(0,0,0,0.06)";
                            e.currentTarget.style.borderColor = `${card.accentColor}35`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.borderColor =
                              "rgba(0, 0, 0, 0.06)";
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              marginBottom: "6px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "14px",
                                  fontWeight: 700,
                                  color: card.accentColor,
                                }}
                              >
                                {card.title}
                              </span>
                              {card.badge && (
                                <span
                                  style={{
                                    backgroundColor: card.accentColor,
                                    color: "#FFFFFF",
                                    fontSize: "10px",
                                    fontWeight: 700,
                                    padding: "2px 7px",
                                    borderRadius: "10px",
                                    letterSpacing: "0.4px",
                                    textTransform: "uppercase",
                                    display: "inline-block",
                                  }}
                                >
                                  {card.badge}
                                </span>
                              )}
                            </div>
                            <img
                              src={card.image}
                              alt=""
                              style={{
                                width: "22px",
                                height: "22px",
                                opacity: 0.85,
                              }}
                              onError={(e) => (e.target.style.display = "none")}
                            />
                          </div>
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#475569",
                              lineHeight: "1.4",
                            }}
                          >
                            {card.subtitle}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom Input Area & Disclaimer */}
        <div
          style={{
            flexShrink: 0,
            padding: "14px 20px 22px 20px",
            maxWidth: "920px",
            width: "100%",
            margin: "0 auto",
            backgroundColor: "#FFFFFF",
            position: "relative",
            zIndex: 5,
            boxSizing: "border-box",
          }}
        >
          {step === "active" && (
            <div>
              {/* After 1st click / send: show template pills above input (Referencing chatinput.png & chattemplates.png) */}
              {messages.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    overflowX: "auto",
                    overflowY: "hidden",
                    paddingBottom: "10px",
                    scrollbarWidth: "none",
                    WebkitOverflowScrolling: "touch",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      margin: "0 auto",
                      flexShrink: 0,
                    }}
                  >
                    {promptOptions.map((card) => (
                      <button
                        key={card.id}
                        onClick={() => handleSelectTemplate(card)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          flexShrink: 0,
                          padding: "4px 10px",
                          backgroundColor:
                            selectedPrompt?.id === card.id
                              ? "#F0F9FF"
                              : "#FFFFFF",
                          border:
                            selectedPrompt?.id === card.id
                              ? "1px solid #2BA9D4"
                              : "1px solid #E2E8F0",
                          borderRadius: "8px",
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#242E49",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.03)",
                          transition: "all 0.15s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = "#2BA9D4";
                          e.currentTarget.style.backgroundColor = "#F8FAFC";
                        }}
                        onMouseLeave={(e) => {
                          const isSelected = selectedPrompt?.id === card.id;
                          e.currentTarget.style.borderColor = isSelected
                            ? "#2BA9D4"
                            : "#E2E8F0";
                          e.currentTarget.style.backgroundColor = isSelected
                            ? "#F0F9FF"
                            : "#FFFFFF";
                        }}
                      >
                        {card.icon}
                        <span>{card.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Retained Chat Input Box style (Referencing chatinput.png & existing .chatInput-Container) */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (isSendActive) {
                    handleSendMessage();
                  }
                }}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: "#FFFFFF",
                  padding: "14px 16px 12px 16px",
                  borderRadius: "16px",
                  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.04)",
                  border: "1px solid #E1E7EA",
                  marginBottom: "12px",
                  minHeight: "90px",
                  justifyContent: "space-between",
                }}
              >
                {selectedPrompt && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      backgroundColor: "#F1F4F7",
                      borderRadius: "15px 15px 0 0",
                      borderBottom: "1px solid #E4E9EE",
                      padding: "10px 14px",
                      margin: "-14px -16px 4px -16px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <img
                        src="/assets/images/new-arrow.svg"
                        alt=""
                        style={{ marginLeft: "2px", marginRight: "10px" }}
                      />
                      <span
                        style={{
                          fontFamily: '"Source Sans 3", sans-serif',
                          fontWeight: 500,
                          fontSize: "13px",
                          lineHeight: 1,
                          letterSpacing: 0,
                          color: "#49546C",
                        }}
                      >
                        {selectedPrompt.title}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleClearSelectedPrompt}
                      aria-label="Remove selected prompt"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 0,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                      }}
                    >
                      <img
                        src="/assets/images/new-close.svg"
                        alt=""
                        style={{ marginRight: "2px" }}
                      />
                    </button>
                  </div>
                )}
                <textarea
                  placeholder={
                    limitReached
                      ? "Free guest limit reached — sign in to continue"
                      : isLoginOnlyPrompt
                      ? "Login for Full Access"
                      : "Ask me anything..."
                  }
                  value={queryInput}
                  onChange={(e) => setQueryInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      if (isSendActive) {
                        handleSendMessage();
                      }
                    }
                  }}
                  disabled={limitReached || isAiResponding}
                  rows={2}
                  style={{
                    width: "100%",
                    border: "none",
                    outline: "none",
                    fontSize: "15px",
                    color: "#1E293B",
                    background: "transparent",
                    resize: "none",
                    fontFamily: "inherit",
                    paddingTop: selectedPrompt ? "8px" : 0,
                  }}
                />
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button
                    type="submit"
                    disabled={!isSendActive}
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "8px",
                      backgroundColor: isSendActive ? "#2BA9D4" : "#8A8FA3",
                      color: "#FFFFFF",
                      border: "none",
                      cursor: isSendActive ? "pointer" : "default",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "background 0.2s",
                    }}
                  >
                    <Send size={16} />
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Interactive Disclaimer rendering Terms and Privacy Policy modals */}
          <div
            style={{ textAlign: "center", fontSize: "12px", color: "#94A3B8" }}
          >
            <span
              onClick={() => {
                setPolicyModalType("tnc");
                setPolicyModalOpen(true);
              }}
              style={{
                cursor: "pointer",
                textDecoration: "underline",
                color: "#64748B",
              }}
            >
              Terms
            </span>{" "}
            and{" "}
            <span
              onClick={() => {
                setPolicyModalType("privacy");
                setPolicyModalOpen(true);
              }}
              style={{
                cursor: "pointer",
                textDecoration: "underline",
                color: "#64748B",
              }}
            >
              Privacy Policy
            </span>{" "}
            apply. Yooki is AI and can make mistakes
          </div>
        </div>
      </div>

      {/* ──────────────── Age Verification Modal ──────────────── */}
      {step === "age_modal" && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15, 23, 42, 0.5)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            zIndex: 9998,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "#FFFFFF",
              borderRadius: "16px",
              maxWidth: "420px",
              width: "100%",
              padding: "32px",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
              position: "relative",
            }}
          >
            <button
              onClick={() => setStep("name_prompt")}
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                background: "#F1F5F9",
                border: "none",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748B",
              }}
            >
              <X size={16} />
            </button>

            <h3
              style={{
                fontSize: "20px",
                fontWeight: 700,
                color: "#1E293B",
                margin: "0 0 6px 0",
              }}
            >
              Hi <em>{preferredName}</em>! 👋
            </h3>
            <p
              style={{
                fontSize: "15px",
                color: "#64748B",
                margin: "0 0 24px 0",
              }}
            >
              Before we get started...
            </p>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                marginBottom: "24px",
              }}
            >
              <label
                onClick={() => setAgeSelection("18_plus")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "14px 16px",
                  borderRadius: "10px",
                  border:
                    ageSelection === "18_plus"
                      ? "2px solid #2BA9D4"
                      : "1px solid #E2E8F0",
                  backgroundColor:
                    ageSelection === "18_plus" ? "#F0F9FF" : "#FFFFFF",
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: 500,
                  color: "#1E293B",
                }}
              >
                <input
                  type="radio"
                  name="age"
                  checked={ageSelection === "18_plus"}
                  onChange={() => setAgeSelection("18_plus")}
                  style={{ accentColor: "#2BA9D4" }}
                />
                <span>I am 18 years old or over</span>
              </label>

              <label
                onClick={() => setAgeSelection("under_18")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "14px 16px",
                  borderRadius: "10px",
                  border:
                    ageSelection === "under_18"
                      ? "2px solid #2BA9D4"
                      : "1px solid #E2E8F0",
                  backgroundColor:
                    ageSelection === "under_18" ? "#FFF1F2" : "#FFFFFF",
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: 500,
                  color: "#1E293B",
                }}
              >
                <input
                  type="radio"
                  name="age"
                  checked={ageSelection === "under_18"}
                  onChange={() => setAgeSelection("under_18")}
                  style={{ accentColor: "#2BA9D4" }}
                />
                <span>I am under 18 years old</span>
              </label>
            </div>

            <button
              onClick={handleAgeContinue}
              disabled={!ageSelection}
              style={{
                width: "100%",
                height: "42px",
                backgroundColor: ageSelection ? "#2BA9D4" : "#E2E8F0",
                color: "#FFFFFF",
                border: "none",
                borderRadius: "8px",
                fontSize: "15px",
                fontWeight: 600,
                cursor: ageSelection ? "pointer" : "default",
                transition: "background 0.2s",
              }}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* ──────────────── Today's Current Affairs Preview Modal ──────────────── */}
      {todayCaModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15, 23, 42, 0.5)",
            backdropFilter: "blur(6px)",
            zIndex: 9997,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setTodayCaModal(false);
          }}
        >
          <div
            style={{
              backgroundColor: "#FFFFFF",
              borderRadius: "20px",
              maxWidth: "680px",
              width: "100%",
              maxHeight: "88vh",
              display: "flex",
              flexDirection: "column",
              padding: "28px 30px",
              boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25)",
              position: "relative",
              boxSizing: "border-box",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "18px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "12px",
                    background: "#FFF7ED",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid rgba(234, 88, 12, 0.2)",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src="/assets/images/menu_icon_currentaffair.svg"
                    alt="Current Affairs"
                    style={{
                      width: "24px",
                      height: "24px",
                      objectFit: "contain",
                    }}
                  />
                </div>
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <h3
                      style={{
                        fontSize: "20px",
                        fontWeight: 700,
                        margin: 0,
                        color: "#0F172A",
                        letterSpacing: "-0.3px",
                      }}
                    >
                      Current Affairs
                    </h3>
                    <span
                      style={{
                        backgroundColor: "#EA580C",
                        color: "#FFFFFF",
                        fontSize: "10px",
                        fontWeight: 700,
                        padding: "2px 7px",
                        borderRadius: "10px",
                        letterSpacing: "0.4px",
                        textTransform: "uppercase",
                        display: "inline-block",
                      }}
                    >
                      Preview
                    </span>
                  </div>
                  <p
                    style={{
                      margin: "2px 0 0 0",
                      fontSize: "13px",
                      color: "#64748B",
                    }}
                  >
                    Today's editorial analysis, UPSC syllabus mapping & key
                    briefs
                  </p>
                </div>
              </div>
              <button
                onClick={() => setTodayCaModal(false)}
                aria-label="Close"
                style={{
                  background: "#F1F5F9",
                  border: "none",
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#64748B",
                  transition: "background 0.15s",
                  flexShrink: 0,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#E2E8F0")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#F1F5F9")
                }
              >
                <X size={16} />
              </button>
            </div>

            <div style={{ overflowY: "auto", flex: 1, paddingRight: "4px" }}>
              {loadingCa ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#64748B",
                  }}
                >
                  Loading articles...
                </div>
              ) : todayArticles.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#64748B",
                  }}
                >
                  No articles available for today yet.
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "14px",
                  }}
                >
                  {todayArticles.map((art) => (
                    <div
                      key={art.id}
                      style={{
                        padding: "16px",
                        backgroundColor: "#F8FAFC",
                        borderRadius: "10px",
                        border: "1px solid #E2E8F0",
                      }}
                    >
                      <h4
                        style={{
                          fontSize: "15px",
                          fontWeight: 700,
                          color: "#0284C7",
                          margin: "0 0 6px 0",
                        }}
                      >
                        {art.topic}
                      </h4>
                      <p
                        style={{
                          fontSize: "13px",
                          color: "#475569",
                          margin: 0,
                          lineHeight: "1.5",
                        }}
                      >
                        {art.content}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              style={{
                marginTop: "16px",
                paddingTop: "14px",
                borderTop: "1px solid #E2E8F0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div
                style={{ display: "flex", flexDirection: "column", gap: "2px" }}
              >
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#1E293B",
                  }}
                >
                  Unlock 360° Editorial Analysis & Thematic Archives
                </span>
                <span style={{ fontSize: "12px", color: "#64748B" }}>
                  Synthesize past relevant articles, GS paper mapping & practice
                  Prelims/Mains PYQs
                </span>
              </div>
              <button
                onClick={() => {
                  setTodayCaModal(false);
                  setCanDismissLogin(true);
                  setLoginModalOpen(true);
                }}
                style={{
                  padding: "9px 18px",
                  backgroundColor: "#2BA9D4",
                  color: "#FFFFFF",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  flexShrink: 0,
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#2293BA")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#2BA9D4")
                }
              >
                <span>Log in for Full Access</span>
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ──────────────── Feature Preview Modal (Notes, Tests, Syllabus, Planner) ──────────────── */}
      {previewModalKey && MENU_PREVIEWS[previewModalKey] && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15, 23, 42, 0.65)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
            padding: "16px",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setPreviewModalKey(null);
          }}
        >
          {(() => {
            const preview = MENU_PREVIEWS[previewModalKey];
            return (
              <div
                style={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "20px",
                  maxWidth: "680px",
                  width: "100%",
                  maxHeight: "88vh",
                  display: "flex",
                  flexDirection: "column",
                  padding: "28px 30px",
                  boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25)",
                  position: "relative",
                  boxSizing: "border-box",
                  animation: "fadeIn 0.2s ease-out",
                }}
              >
                {/* Modal Header */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "18px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <div
                      style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "12px",
                        background: preview.accentBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: `1px solid ${preview.accent}30`,
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={preview.icon}
                        alt={preview.title}
                        style={{ width: "24px", height: "24px" }}
                      />
                    </div>
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <h3
                          style={{
                            fontSize: "20px",
                            fontWeight: 700,
                            margin: 0,
                            color: "#0F172A",
                            letterSpacing: "-0.3px",
                          }}
                        >
                          {preview.title}
                        </h3>
                        <img
                          src="/assets/images/pro-button-gradient-1.svg"
                          alt="Pro"
                          style={{ height: "18px", width: "auto" }}
                        />
                      </div>
                      <p
                        style={{
                          margin: "2px 0 0 0",
                          fontSize: "13px",
                          color: "#64748B",
                        }}
                      >
                        {preview.tagline}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setPreviewModalKey(null)}
                    aria-label="Close"
                    style={{
                      background: "#F1F5F9",
                      border: "none",
                      borderRadius: "50%",
                      width: "32px",
                      height: "32px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#64748B",
                      transition: "background 0.15s",
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#E2E8F0")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "#F1F5F9")
                    }
                  >
                    <X size={16} />
                  </button>
                </div>

                {/* Modal Scrollable Body */}
                <div
                  style={{ overflowY: "auto", flex: 1, paddingRight: "4px" }}
                >
                  {/* Quick Highlights / Stats Strip */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, 1fr)",
                      gap: "10px",
                      marginBottom: "18px",
                    }}
                  >
                    {preview.stats.map((st, i) => (
                      <div
                        key={i}
                        style={{
                          background: "#F8FAFC",
                          border: "1px solid #E2E8F0",
                          borderRadius: "10px",
                          padding: "10px 12px",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color: preview.accent,
                          }}
                        >
                          {st.val}
                        </div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#64748B",
                            fontWeight: 500,
                            marginTop: "2px",
                          }}
                        >
                          {st.label}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Sample Mock UI Cards */}
                  <div style={{ marginBottom: "18px" }}>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#475569",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: "8px",
                      }}
                    >
                      Live Feature Sample
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {preview.cards.map((c, i) => (
                        <div
                          key={i}
                          style={{
                            background: "#FFFFFF",
                            border: "1px solid #E2E8F0",
                            borderRadius: "12px",
                            padding: "14px 16px",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "11px",
                                fontWeight: 700,
                                color: c.tagColor,
                                background: c.tagBg,
                                padding: "2px 8px",
                                borderRadius: "6px",
                                letterSpacing: "0.2px",
                              }}
                            >
                              {c.tag}
                            </span>
                            <span
                              style={{ fontSize: "11px", color: "#94A3B8" }}
                            >
                              {c.date}
                            </span>
                          </div>
                          <h4
                            style={{
                              margin: "2px 0 0 0",
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#1E293B",
                            }}
                          >
                            {c.title}
                          </h4>
                          <p
                            style={{
                              margin: 0,
                              fontSize: "12.5px",
                              color: "#64748B",
                              lineHeight: "1.45",
                            }}
                          >
                            {c.snippet}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Capabilities List */}
                  <div>
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#475569",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        marginBottom: "8px",
                      }}
                    >
                      What's Included in Pro
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {preview.features.map((f, i) => (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "10px",
                            background: "#F8FAFC",
                            padding: "10px 12px",
                            borderRadius: "10px",
                            border: "1px solid #F1F5F9",
                          }}
                        >
                          <CheckCircle2
                            size={16}
                            color={preview.accent}
                            style={{ marginTop: "2px", flexShrink: 0 }}
                          />
                          <div
                            style={{ fontSize: "12.5px", lineHeight: "1.4" }}
                          >
                            <strong style={{ color: "#1E293B" }}>
                              {f.title}:{" "}
                            </strong>
                            <span style={{ color: "#64748B" }}>{f.desc}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Modal Footer CTA */}
                <div
                  style={{
                    marginTop: "18px",
                    paddingTop: "14px",
                    borderTop: "1px solid #E2E8F0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  <span style={{ fontSize: "12px", color: "#64748B" }}>
                    Sign in to unlock full {preview.title} access
                  </span>
                  <button
                    onClick={() => {
                      setPreviewModalKey(null);
                      setCanDismissLogin(true);
                      setLoginModalOpen(true);
                    }}
                    style={{
                      padding: "8px 18px",
                      backgroundColor: "#2BA9D4",
                      color: "#FFFFFF",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = "#2293BA")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "#2BA9D4")
                    }
                  >
                    <span>Log in for Full Access</span>
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ──────────────── Terms & Privacy Policy Markdown Viewer Modal ──────────────── */}
      <PolicyModal
        isOpen={policyModalOpen}
        onClose={() => setPolicyModalOpen(false)}
        type={policyModalType}
      />

      {/* ──────────────── Festive Holiday Theme Overlay (DB Driven) ──────────────── */}
      <FestiveThemeOverlay theme={festiveTheme} />

      {/* ──────────────── Login Modal Overlay ──────────────── */}
      <LoginModal
        isOpen={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        canDismiss={canDismissLogin}
        onLoginSuccess={() => {
          navigate("/dashboard");
        }}
      />
    </div>
  );
};

export default Homepage;
